import * as THREE from 'three';
import { sunDirectionFromAngles } from '@/utils/sun';

export type ContactShadowOptions = {
  /** Shadow radius (m) when the ball is on the ground. */
  radius?: number;
  /** Peak opacity on the ground. */
  opacity?: number;
  /** Ball height (m) at which the shadow has fully faded. */
  fadeHeight?: number;
  /** Direction the sunlight travels — points downward. See utils/sun. */
  sunDirection?: THREE.Vector3;
  /**
   * Ceiling on how far the shadow may lean from directly under the ball (m).
   *
   * A true projection runs away fast: a 40-degree sun puts the shadow of a ball
   * 6 m up nearly 10 m downwind, which is correct and useless — this shadow's
   * whole job is telling you where the ball is over the ground. The default is
   * chosen so the cap does not bind anywhere the shadow is actually legible:
   * it fades out by `fadeHeight`, and below roughly a metre and a half — where
   * it is at its most opaque and most read — the true offset is under the cap
   * and the projection is exact.
   */
  maxOffset?: number;
};

/**
 * A fake ball shadow for MR: an unlit radial-gradient quad that follows the
 * ball's ground position, growing softer and fainter with height. With the
 * real shadow pass disabled in the MR range, this is the strongest depth cue
 * available — and because it's a dark translucent decal it even reads as a
 * shadow over the passthrough floor inside the mask's bay (it is
 * mask-exempt by design).
 */
export class ContactShadow {
  object: THREE.Mesh;

  #radius: number;
  #opacity: number;
  #fadeHeight: number;
  #maxOffset: number;
  #material: THREE.MeshBasicMaterial;
  #sun = new THREE.Vector3(...sunDirectionFromAngles());
  #up = new THREE.Vector3(0, 1, 0);

  constructor(options: ContactShadowOptions = {}) {
    this.#radius = options.radius ?? 0.055;
    this.#opacity = options.opacity ?? 0.4;
    this.#fadeHeight = options.fadeHeight ?? 6;
    this.#maxOffset = options.maxOffset ?? 2;
    if (options.sunDirection) this.#sun.copy(options.sunDirection).normalize();

    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 128;
    const ctx = canvas.getContext('2d')!;
    const gradient = ctx.createRadialGradient(64, 64, 8, 64, 64, 64);
    gradient.addColorStop(0, 'rgba(0,0,0,1)');
    gradient.addColorStop(0.6, 'rgba(0,0,0,0.55)');
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 128, 128);

    const texture = new THREE.CanvasTexture(canvas);
    this.#material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      fog: false,
    });
    this.object = new THREE.Mesh(
      new THREE.PlaneGeometry(this.#radius * 2, this.#radius * 2).rotateX(-Math.PI / 2),
      this.#material
    );
    this.object.name = 'ContactShadow';
    this.object.userData.mrMaskExempt = true;
    this.object.renderOrder = 2; // over the ground, under the trail
    this.object.visible = false;
  }

  /**
   * Track the ball each frame. Positions in the same space as the ball
   * (sim/worldRoot-local); groundY defaults to the flat range floor.
   */
  update(
    ballPosition: THREE.Vector3,
    visible: boolean,
    groundY = 0,
    groundNormal?: THREE.Vector3,
  ) {
    const height = Math.max(0, ballPosition.y - groundY);
    const fade = 1 - Math.min(1, height / this.#fadeHeight);
    if (!visible || fade <= 0) {
      this.object.visible = false;
      return;
    }
    this.object.visible = true;

    // Follow the light down to the ground rather than dropping straight. A ball
    // sitting on the turf casts its shadow slightly to one side, and that
    // offset — small, but present and consistent with everything else the sun
    // lights — is most of what separates a shadow from a dark sticker.
    let offsetX = 0;
    let offsetZ = 0;
    if (this.#sun.y < -1e-3) {
      const t = height / -this.#sun.y;
      offsetX = this.#sun.x * t;
      offsetZ = this.#sun.z * t;
      const lean = Math.hypot(offsetX, offsetZ);
      if (lean > this.#maxOffset) {
        const k = this.#maxOffset / lean;
        offsetX *= k;
        offsetZ *= k;
      }
    }

    // groundY is sampled under the BALL, not under the offset shadow. Over a
    // lean this short the terrain barely moves, and this is a soft blob — not
    // worth a second raycast per frame to chase a centimetre.
    this.object.position.set(
      ballPosition.x + offsetX,
      groundY + 0.004,
      ballPosition.z + offsetZ,
    );

    // Lie along the slope. Without this the quad buries one edge in a bank and
    // floats the other, which on a green reads as a hovering square.
    if (groundNormal) {
      this.object.quaternion.setFromUnitVectors(this.#up, groundNormal);
      this.object.position.addScaledVector(groundNormal, 0.004);
    } else {
      this.object.quaternion.identity();
    }

    const spread = 1 + height * 0.6;
    this.object.scale.setScalar(spread);
    this.#material.opacity = this.#opacity * fade / spread;
  }

  /** Direction the sunlight travels (points downward); normalised on the way in. */
  setSunDirection(direction: THREE.Vector3) {
    if (direction.lengthSq() > 1e-6) this.#sun.copy(direction).normalize();
  }

  dispose() {
    this.object.geometry.dispose();
    this.#material.map?.dispose();
    this.#material.dispose();
  }
}
